import React, { useEffect, useRef, useState } from "react";
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useHover,
    useFocus,
    useDismiss,
    useRole,
    useInteractions,
    FloatingPortal,
    arrow,
} from "@floating-ui/react";
import { useIntl } from "react-intl";

type OwIconTooltipProps = {
    iconClassName: string;
    tooltipContent: React.ReactNode;
    placement?: "top" | "bottom" | "left" | "right";
    offsetDistance?: number;
    additionalIconProps?: React.HTMLAttributes<HTMLElement>;
    inModal?: boolean;
    translateContent?: boolean;
};

export const OwIconTooltip = ({
    iconClassName,
    tooltipContent,
    placement = "top",
    offsetDistance = 10,
    additionalIconProps = {},
    inModal = false,
    translateContent = false,
}: OwIconTooltipProps) => {
    const intl = useIntl();

    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const iconRef = useRef<HTMLElement>(null);
    const arrowRef = useRef<HTMLDivElement>(null);

    // Setup floating-ui
    const { x, y, strategy, refs, middlewareData, context } = useFloating({
        open: isTooltipOpen,
        onOpenChange: setIsTooltipOpen,
        placement,
        middleware: [offset(offsetDistance), flip(), shift(), arrow({ element: arrowRef })],
        whileElementsMounted: autoUpdate,
    });

    if (translateContent && typeof tooltipContent === "string") {
        tooltipContent = (
            <div className="text-wrap">
                {intl.formatMessage({
                    id: tooltipContent || "",
                    defaultMessage: tooltipContent || "",
                    description: "tooltip",
                })}
            </div>
        );
    }

    // Setup interactions for the tooltip
    const hover = useHover(context, { move: false });
    const focus = useFocus(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "tooltip" });

    // Merge all interactions
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

    // Connect our refs
    useEffect(() => {
        if (iconRef.current) {
            refs.setReference(iconRef.current);
        }
    }, [refs, iconRef]);

    // Combine props from parent with our reference props
    const combinedIconProps = {
        ...additionalIconProps,
        ...getReferenceProps(),
        className: iconClassName,
    };

    return (
        <>
            <i ref={iconRef} {...combinedIconProps} />
            {isTooltipOpen && (
                <FloatingPortal>
                    <div
                        className="tooltip-container p-2 tooltip-bg tooltip-text rounded shadow-sm"
                        ref={refs.setFloating}
                        style={{
                            position: strategy,
                            top: y ?? 0,
                            left: x ?? 0,
                            width: "max-content",
                            maxWidth: "250px",
                            zIndex: inModal ? 999999999 : 1000,
                        }}
                        {...getFloatingProps()}>
                        {tooltipContent}
                        <div
                            ref={arrowRef}
                            className="tooltip-arrow"
                            style={{
                                position: "absolute",
                                width: "8px",
                                height: "8px",
                                background: "inherit",
                                visibility: middlewareData.arrow ? "visible" : "hidden",
                                transform: "rotate(45deg)",
                                top: middlewareData.arrow?.y != null ? middlewareData.arrow.y : "",
                                left: middlewareData.arrow?.x != null ? middlewareData.arrow.x : "",
                                [placement.includes("top")
                                    ? "bottom"
                                    : placement.includes("bottom")
                                      ? "top"
                                      : placement.includes("left")
                                        ? "right"
                                        : "left"]: "-4px",
                            }}
                        />
                    </div>
                </FloatingPortal>
            )}
        </>
    );
};
